import React, { useEffect, useState } from 'react';
import {
  FiActivity,
  FiBarChart2,
  FiClock,
  FiDatabase,
  FiFileText,
  FiImage,
  FiMessageSquare,
  FiUploadCloud,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { workspaceService } from '../services/aiService';

function KIWorkspace() {
  const [workspaceStats, setWorkspaceStats] = useState({
    totalDocuments: 0,
    totalConversations: 0,
    totalAnalyses: 0,
    lastActivity: null,
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await workspaceService.getStats();
        if (data.success) {
          setWorkspaceStats({
            totalDocuments: data.totalDocuments,
            totalConversations: data.totalConversations,
            totalAnalyses: data.totalAnalyses,
            lastActivity: data.lastActivity,
          });
        }
      } catch (error) {
        console.error('Fehler beim Laden der Statistiken:', error);
        // Fallback zu Mock-Daten bei API-Fehler
        setWorkspaceStats({
          totalDocuments: 24,
          totalConversations: 156,
          totalAnalyses: 89,
          lastActivity: new Date().toISOString(),
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const modules = [
    {
      id: 'chat',
      title: 'Chat',
      description: 'Dokumente hochladen und mit KI chatten',
      icon: FiMessageSquare,
      color: 'bg-blue-100 dark:bg-blue-900/20',
      route: '/ki-workspace/chat',
      stats: {
        documents: workspaceStats.totalDocuments,
        conversations: workspaceStats.totalConversations,
      },
    },
    {
      id: 'datenanalyse',
      title: 'Datenanalyse',
      description: 'CSV-Daten analysieren und Vorhersagen erstellen',
      icon: FiBarChart2,
      color: 'bg-green-100 dark:bg-green-900/20',
      route: '/ki-workspace/datenanalyse',
      stats: {
        analyses: workspaceStats.totalAnalyses,
      },
    },
    {
      id: 'bildanalyse',
      title: 'Bildanalyse',
      description: 'Bilder auf Defekte und Performance prüfen',
      icon: FiImage,
      color: 'bg-purple-100 dark:bg-purple-900/20',
      route: '/ki-workspace/bildanalyse',
      stats: {
        images: 12,
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">🤖 KI-Workspace</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Intelligente Analyse und Dokumentenverarbeitung
        </p>
      </div>

      {/* Statistiken */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <FiDatabase className="text-blue-400 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Dokumente</p>
              <p className="text-2xl font-bold">{workspaceStats.totalDocuments}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <FiMessageSquare className="text-green-400 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gespräche</p>
              <p className="text-2xl font-bold">{workspaceStats.totalConversations}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <FiBarChart2 className="text-purple-400 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Analysen</p>
              <p className="text-2xl font-bold">{workspaceStats.totalAnalyses}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center">
            <FiClock className="text-orange-400 text-xl mr-3" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Letzte Aktivität</p>
              <p className="text-sm font-medium">
                {workspaceStats.lastActivity
                  ? new Date(workspaceStats.lastActivity).toLocaleDateString('de-DE')
                  : 'Heute'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Module Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {modules.map((module) => {
          const IconComponent = module.icon;
          // Chat-Modul in neuem Fenster öffnen
          const handleClick = (e) => {
            if (module.id === 'chat') {
              e.preventDefault();
              window.open(module.route, '_blank', 'noopener,noreferrer');
            }
          };

          return (
            <Link
              key={module.id}
              to={module.route}
              onClick={handleClick}
              className="group bg-white dark:bg-gray-800 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200 transform hover:scale-105 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            >
              <div className="flex items-center mb-4">
                <div
                  className={`${module.color} w-10 h-10 rounded-lg mr-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}
                >
                  <IconComponent className="text-xl" />
                </div>
                <h3 className="text-lg font-semibold">{module.title}</h3>
              </div>

              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{module.description}</p>

              <div className="flex justify-between items-center">
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  {Object.entries(module.stats).map(([key, value]) => (
                    <span key={key} className="mr-3">
                      {value} {key}
                    </span>
                  ))}
                </div>
                <div className="text-blue-400 group-hover:text-blue-300 transition-colors">
                  <FiActivity className="text-sm" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Schnellaktionen */}
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">⚡ Schnellaktionen</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/ki-workspace/chat"
            onClick={(e) => {
              e.preventDefault();
              window.open('/ki-workspace/chat', '_blank', 'noopener,noreferrer');
            }}
            className="flex items-center p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <FiUploadCloud className="mr-3" />
            <span>Dokument hochladen</span>
          </Link>
          <Link
            to="/ki-workspace/datenanalyse"
            className="flex items-center p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <FiBarChart2 className="mr-3" />
            <span>Daten analysieren</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default KIWorkspace;
